<template>
  <add-image v-if="imageForActions === '' && imageResult === ''">
    <div style="text-align: center; margin-bottom: 12px; color: var(--secondary-100);">{{ $t('menu.remove-bg') }}</div>
    <img :src="`${baseUrl}/images/multi-menu/remove-background.png`" alt="">
  </add-image>
  <ai-drop-loader v-if="loading" />
  <div
    v-else-if="imageForActions !== '' && imageResult === ''"
    :class="{ 'service__container--mobile': isMobile }"
    class="service__container remove-background"
  >
    <div class="remove-background-edit">
      <image-finetune
        class="remove-background-edit__finetune"
        v-if="isEdit"
        :src="imageForActions"
        @reset="isEdit = false"
        @save="editHandler"
        :actions="['reset', 'save']"
      />
      <template v-else>
        <div class="remove-background-edit__settings">
          <div class="remove-background-edit__tools">
            <div class="remove-background-edit__tools__item">
              <span>{{ $t('common.brush-type') }}</span>
              <div v-if="!isMobile" class="remove-background-edit__controllers">
                <ai-tooltip content-class="remove-background-edit__tooltip" top location="top">
                  <template #top>
                    <ai-clear-button
                      :class="{ 'remove-background-edit__controller--active': mode === 'erase' }"
                      class="remove-background-edit__controller"
                      @click="setMode('erase')"
                    >
                      <img :src="require('@/assets/icons/editor/removeBg.svg')" alt="#" />
                    </ai-clear-button>
                  </template>
                  <template #content>
                    <span>{{ $t('image-editor.erase') }}</span>
                    {{ $t('image-editor.erase-tooltip-text') }}
                  </template>
                </ai-tooltip>
                <ai-tooltip content-class="remove-background-edit__tooltip" top location="top">
                  <template #top>
                    <ai-clear-button
                      :class="{ 'remove-background-edit__controller--active': mode === 'restore' }"
                      class="remove-background-edit__controller"
                      @click="setMode('restore')"
                    />
                  </template>
                  <template #content>
                    <span>{{ $t('image-editor.restore-brush') }}</span>
                    {{ $t('image-editor.restore-tooltip-text') }}
                  </template>
                </ai-tooltip>
              </div>
              <div v-if="isMobile" class="remove-background-edit__controllers">
                <ai-clear-button
                  :class="{ 'remove-background-edit__controller--active': mode === 'erase' }"
                  class="remove-background-edit__controller"
                  @click="setMode('erase')"
                >
                  <img :src="require('@/assets/icons/editor/removeBg.svg')" alt="#" />
                </ai-clear-button>
                <ai-clear-button
                  :class="{ 'remove-background-edit__controller--active': mode === 'restore' }"
                  class="remove-background-edit__controller"
                  @click="setMode('restore')"
                />
              </div>
            </div>
            <v-spacer />
            <div class="remove-background-edit__tools__item">
              <span>{{ $t('common.size') }}</span>
              <v-slider :min="0" :max="220" v-model.number="brushSize" style="width: 100px" />
            </div>
          </div>
        </div>
        <div class="remove-background-edit__workarea">
          <ai-card :radius="16" class="remove-background-edit__workarea__card">
            <div class="remove-background-edit__workarea__container">
              <div class="remove-background-edit__canvas__zoom">
                <ai-clear-button
                  class="remove-background-edit__canvas__zoom__button"
                  :active-zoom="activeZoom"
                  @click="zoomOutHandler"
                >
                  <inline-svg :src="require('@/assets/icons/IconDecrease.svg')" />
                </ai-clear-button>
                <ai-clear-button class="remove-background-edit__canvas__zoom__button" @click="zoomInHandler">
                  <inline-svg :src="require('@/assets/icons/IconIncrease.svg')" />
                </ai-clear-button>
              </div>
              <div class="remove-background-edit__canvas__image__container">
                <ai-canvas
                  :images="[]"
                  :key="key"
                  :width="imageWidth"
                  :height="imageHeight"
                  :background="imageForActions"
                  :restore-background="sourceBgImage"
                  :mode="mode"
                  ref="aiCanvasRef"
                  :brush-size="brushSize"
                  :active-zoom="activeZoom"
                  @active-zoom-set="activeZoomSetHandler"
                />
              </div>
              <ai-drop-loader v-if="generationLoading" />
            </div>
            <div class="remove-background-edit__workarea__data">
              <v-spacer />
              <div class="remove-background-edit__workarea__actions">
                <ai-button theme="dark" @click="imageForActions = ''; sourceBgImage = ''">{{ $t('services.reset') }}</ai-button>
                <ai-button theme="dark" @click="toEditHadler">{{ $t('common.edit') }}</ai-button>
                <ai-button
                  v-if="!isMobile"
                  :disabled="generationLoading"
                  theme="gradient-primary"
                  class="remove-background-edit__workarea__button"
                  @click="removeBackgroundFileHandler"
                >
                  <template v-if="!generationLoading">
                    {{ $t('remove-background.remove-background') }}
                  </template>
                  <time-counter v-else />
                </ai-button>
              </div>
            </div>
            <ai-button
              v-if="isMobile"
              theme="gradient-primary"
              :disabled="generationLoading"
              @click="removeBackgroundFileHandler"
              class="remove-background-edit__settings__button"
            >
              <template v-if="!generationLoading">
                {{ $t('remove-background.remove-background') }}
              </template>
              <time-counter v-else />
            </ai-button>
          </ai-card>
        </div>
      </template>
    </div>
  </div>
  <result-image v-else-if="imageForActions !== '' && imageResult !== ''" />
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useEventBus } from '@vueuse/core';

import { useCommonStore } from '@/store/common';
import { useServicesStore } from '@/store/services/index';

// import { getBase64Size } from '@/helpers/getBase64Size';
import { removeBase64Prefix } from '@/helpers/removeBase64Prefix';

import { TMode } from '@/types/TMode';

import AddImage from '@/components/common/AddImage/AddImage.vue';
import ImageFinetune from '@/components/common/ImageFinetune/ImageFinetune.vue';
import ResultImage from '@/components/common/ResultImage/ResultImage.vue';
import TimeCounter from '@/components/common/TimeCounter/TimeCounter.vue';

import AiButton from '@/components/UI/AiButton/AiButton.vue';
import AiClearButton from '@/components/UI/AiClearButton/AiClearButton.vue';
import AiCanvas from '@/components/UI/AiCanvas/AiCanvas.vue';
import AiCard from '@/components/UI/AiCard/AiCard.vue';
import AiDropLoader from '@/components/UI/AiDropLoader/AiDropLoader.vue';
import AiTooltip from '@/components/UI/AiTooltip/AiTooltip.vue';

const busError = useEventBus<string>('toast-error');

const { t } = useI18n();
const baseUrl = process.env.VUE_APP_BASE_URL;

const { isMobile } = storeToRefs(useCommonStore());

const { generateImage } = useServicesStore();

const { imageForActions, sourceBgImage, imageResult, imagePrev } = storeToRefs(useServicesStore());

const generationLoading = ref<boolean>(false);
const imageWidth = ref<number>(0);
const imageHeight = ref<number>(0);
const isEdit = ref<boolean>(false);
const mode = ref<TMode>('erase');
const brushSize = ref<number>(110);
const aiCanvasRef = ref<any>(null);
const activeZoom = ref<number>(1);
const key = ref<number>(0);
const loading = ref<boolean>(false);

// const imageSize = computed(() => getBase64Size(imageForActions.value));

const removeBackgroundFileHandler = async (): Promise<void> => {
  generationLoading.value = true;
  const canvasImage = await aiCanvasRef.value.getBackground();
  imageForActions.value = canvasImage.enlargedBase64src;
  // generateImage({
  //   action: 'remove_background',
  //   image: removeBase64Prefix(imageForActions.value),
  //   params: {},
  // })
  //   .then((res) => {
  imagePrev.value = sourceBgImage.value;
  imageResult.value = imageForActions.value;
  imageForActions.value = sourceBgImage.value;
  //   })
  //   .catch((e) => {
  //     busError.emit(e?.message || t('common.err-gen'));
  //   })
  //   .finally(() => {
  generationLoading.value = false;
  //   });
};

const imageSizeHandler = (): void => {
  if (imageForActions.value !== '') {
    const img = new Image();
    img.onload = () => {
      imageWidth.value = img.width;
      imageHeight.value = img.height;
      key.value += 1;
    };
    img.src = imageForActions.value;
    // const canvas = document.createElement('canvas');
    // const img1 = new Image();
    // img1.onload = async () => {
    //   const imageWidth1 = img1.width;
    //   const imageHeight1 = img1.height;
    //   if (imageWidth1 / imageHeight1 > 1) {
    //     canvas.width = 512;
    //     canvas.height = (512 * imageHeight1) / imageWidth1;
    //   } else {
    //     canvas.height = 512;
    //     canvas.width = (512 * imageWidth1) / imageHeight1;
    //   }

    //   const ctx = canvas.getContext('2d');
    //   if (ctx) {
    //     ctx.drawImage(img1, 0, 0, canvas.width, canvas.height);
    //     sourceBgImage.value = canvas.toDataURL('image/png');
    //     console.log(sourceBgImage.value);
    //     key.value += 1;
    //   }
    // };
    // img1.src = sourceBgImage.value;
  }
};

const editHandler = (image: string): void => {
  imageForActions.value = image;
  isEdit.value = false;
};

const zoomInHandler = (): void => {
  activeZoom.value += 0.1;
};

const zoomOutHandler = (): void => {
  activeZoom.value -= 0.1;
};

const activeZoomSetHandler = (newVal: number): void => {
  activeZoom.value = newVal;
};

const setMode = (modeVal: TMode): void => {
  mode.value = modeVal;
};

const toEditHadler = async (): Promise<void> => {
  const canvasImage = await aiCanvasRef.value.getBackground();
  imageForActions.value = canvasImage.enlargedBase64src;
  isEdit.value = true;
};

onMounted(async () => {
  if (localStorage.getItem('to-remove-bg') === 'true') {
    localStorage.removeItem('to-remove-bg');
    loading.value = true;
    const image = imageForActions.value;
    imageForActions.value = '';
    try {
      const res = await generateImage({
        action: 'remove_background',
        image: removeBase64Prefix(image),
        params: {},
      });
      imageForActions.value = `data:image/png;base64,${res.payload.notTrimmedImage}`;
    } catch (e: any) {
      busError.emit(e?.error || t('common.err-gen'));
    }
    loading.value = false;
    sourceBgImage.value = image;
  }
  imageSizeHandler();
});

watch(imageForActions, () => {
  key.value += 1;
  imageSizeHandler();
  generationLoading.value = false;
});

watch(sourceBgImage, () => {
  imageSizeHandler();
});

</script>

<style scoped lang="scss" src="./RemoveBackground.scss" />
